import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { clampStatusDetail, sinceMinutesLabel } from '@/features/accounts/attention-labels';
import { cn } from '@/lib/utils';

/**
 * Status -> color mapping. Tailwind tokens chosen so each state is visually
 * distinct AND `qr_required` (orange) is unmistakably *not* `connecting`
 * (amber).
 *
 *   connected    -> emerald-500 (healthy / ready)
 *   connecting   -> amber-500   (transient — pairing in progress)
 *   qr_required  -> orange-500  (action needed — distinct from amber)
 *   down         -> rose-500    (failed / gateway link lost)
 *   banned       -> red-600     (WhatsApp ban — a deeper, alarming red that
 *                                reads as clearly worse than `down`'s rose;
 *                                re-scan will NOT recover it)
 *
 * `label` is the human-readable status name surfaced in the tooltip. `banned`
 * carries the operator-facing verdict directly (「疑似封号 · 重扫无效」) — unlike
 * `qr_required` ("QR re-scan required"), a re-scan does NOT help, so the label
 * says so up front. See `docs/contracts/openclaw-adapter.md` (§Account status).
 */
const STATUS_PRESENTATION: Record<
  string,
  { color: string; label: string }
> = {
  // Neumorphic: each dot carries a same-color 6px glow (static box-shadow —
  // literal arbitrary classes so the JIT scanner picks them up).
  connected: { color: 'bg-emerald-500 shadow-[0_0_6px_#10b981]', label: 'Connected' },
  connecting: { color: 'bg-amber-500 shadow-[0_0_6px_#f59e0b]', label: 'Connecting' },
  qr_required: { color: 'bg-orange-500 shadow-[0_0_6px_#f97316]', label: 'QR re-scan required' },
  down: { color: 'bg-rose-500 shadow-[0_0_6px_#f43f5e]', label: 'Down' },
  banned: { color: 'bg-red-600 shadow-[0_0_6px_#dc2626]', label: '疑似封号 · 重扫无效' },
};

const UNKNOWN_PRESENTATION = { color: 'bg-slate-400', label: 'Unknown' };

interface StatusDotProps {
  status: string;
  className?: string;
  /** `Account.status_since` — when the CURRENT `status` began (2026-09 early
   *  warning, migration 0015). Optional/nullable: an older hub or a locally
   *  built row omits it, in which case the tooltip just skips the "已 N 分钟"
   *  segment (spec 01 §3.5 StatusDot row). */
  statusSince?: string | null;
  /** `Account.status_detail` — the gateway's last status detail for this
   *  account (≤300 chars server-side; clamped further here, ≤120 chars per
   *  spec, so one long gateway error string can't blow out the tooltip). */
  statusDetail?: string | null;
}

/**
 * Small colored circle indicating an account's connection state. Wrapped in a
 * shadcn Tooltip so hovering surfaces the status name (kept short — the dot
 * itself does the visual heavy lifting), plus — 2026-09 — how long the
 * account has been in this status and the gateway's own detail string, when
 * either is available: `label · 已 N 分钟 · status_detail`.
 */
export function StatusDot({
  status,
  className,
  statusSince,
  statusDetail,
}: StatusDotProps): React.JSX.Element {
  const presentation = STATUS_PRESENTATION[status] ?? UNKNOWN_PRESENTATION;

  const sinceText = sinceMinutesLabel(statusSince, Date.now());
  const detailText = clampStatusDetail(statusDetail);
  const tooltipParts = [presentation.label, sinceText, detailText].filter(
    (part): part is string => part !== null,
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // `data-status` lets tests assert "the dot for this status renders"
          // without coupling to the exact Tailwind class names.
          data-status={status}
          aria-label={presentation.label}
          role="status"
          className={cn(
            'inline-block size-2.5 shrink-0 rounded-full',
            presentation.color,
            className,
          )}
        />
      </TooltipTrigger>
      <TooltipContent>{tooltipParts.join(' · ')}</TooltipContent>
    </Tooltip>
  );
}
